// Unit conversion utilities for height and weight measurements

/**
 * Convert pounds to kilograms
 * @param {number} pounds - Weight in pounds
 * @returns {number} Weight in kilograms
 */
export const poundsToKg = (pounds) => {
  return pounds * 0.453592;
};

/**
 * Convert kilograms to pounds
 * @param {number} kg - Weight in kilograms
 * @returns {number} Weight in pounds
 */
export const kgToPounds = (kg) => {
  return kg / 0.453592;
};

/**
 * Convert inches to centimeters
 * @param {number} inches - Height in inches
 * @returns {number} Height in centimeters
 */
export const inchesToCm = (inches) => {
  return inches * 2.54;
};

/**
 * Convert centimeters to inches
 * @param {number} cm - Height in centimeters
 * @returns {number} Height in inches
 */
export const cmToInches = (cm) => {
  return cm / 2.54;
};

/**
 * Convert feet and inches to centimeters
 * @param {number} feet - Height in feet
 * @param {number} inches - Additional inches
 * @returns {number} Height in centimeters
 */
export const feetInchesToCm = (feet, inches) => {
  const totalInches = (feet * 12) + inches;
  return inchesToCm(totalInches);
};

/**
 * Convert centimeters to feet and inches
 * @param {number} cm - Height in centimeters
 * @returns {object} Object with feet and inches properties
 */
export const cmToFeetInches = (cm) => {
  const totalInches = cmToInches(cm);
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { feet, inches };
};

/**
 * Calculate BMI from weight and height
 * @param {number} weight - Weight in kilograms
 * @param {number} height - Height in centimeters
 * @returns {number} BMI value
 */
export const calculateBMI = (weight, height) => {
  if (weight <= 0 || height <= 0) return 0;
  const heightInMeters = height / 100;
  return weight / (heightInMeters * heightInMeters);
};

/**
 * Calculate BMI from US measurements
 * @param {number} weightPounds - Weight in pounds
 * @param {number} heightInches - Height in inches
 * @returns {number} BMI value
 */
export const calculateBMIFromUS = (weightPounds, heightInches) => {
  if (weightPounds <= 0 || heightInches <= 0) return 0;
  const weightKg = poundsToKg(weightPounds);
  const heightCm = inchesToCm(heightInches);
  return calculateBMI(weightKg, heightCm);
};

/**
 * Get BMI category based on BMI value
 * @param {number} bmi - BMI value
 * @returns {string} BMI category
 */
export const getBMICategory = (bmi) => {
  if (bmi < 18.5) return 'underweight';
  if (bmi < 25) return 'normal';
  if (bmi < 30) return 'overweight';
  return 'obese';
};

/**
 * Convert measurement data to standard metric format
 * @param {object} data - Measurement data with value and unit
 * @returns {number} Value in standard metric unit
 */
export const convertToMetric = (data) => {
  if (!data || !data.value || !data.unit) return 0;
  
  const value = parseFloat(data.value);
  if (isNaN(value)) return 0;
  
  switch (data.unit) {
    case 'kg':
      return value;
    case 'lbs':
    case 'pounds':
      return poundsToKg(value);
    case 'cm':
      return value;
    case 'in':
    case 'inches':
      return inchesToCm(value);
    case 'ft':
    case 'feet':
      // Handle feet.inches format (e.g., 5.3 = 5 feet 3 inches)
      const feet = Math.floor(value);
      const inches = Math.round((value - feet) * 10);
      return feetInchesToCm(feet, inches);
    default:
      return value;
  }
};
